import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Home, Plus, Trash2, Download, Upload, LogOut, Search, Filter,
  Phone, Mail, MapPin, Tag, FileText, Edit, X, Save, User, Calendar,
  CheckCircle, XCircle, Shield, ShieldOff, Target, Bot, ChevronRight, Users
} from 'lucide-react';
import { toast } from 'sonner';
import AIAssistant from '../components/AIAssistant';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Debounce helper
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => clearTimeout(handler);
  }, [value, delay]);
  
  return debouncedValue;
};

const CRM = () => {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactDeals, setContactDeals] = useState([]);  // Deals du contact sélectionné
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  const [aiContactId, setAiContactId] = useState(null);
  const [aiContactName, setAiContactName] = useState('');
  const [aiDealId, setAiDealId] = useState(null);
  const [user, setUser] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalContacts, setTotalContacts] = useState(0);
  const contactsPerPage = 20;

  // Filtres
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [filterTags, setFilterTags] = useState('');

  // Formulaire contact
  const [formData, setFormData] = useState({
    prenom: '',
    nom: '',
    email: '',
    telephone: '',
    address: {
      street: '',
      city: '',
      province: 'Québec',
      postal_code: ''
    },
    statut: 'prospect',
    degre_interet: '',
    tags: [],
    interets: [],
    notes: '',
    source: 'manuel',
    marketing_consent: false,
    consent_source: 'manuel'
  });

  // Import Excel
  const [importFile, setImportFile] = useState(null);
  const [duplicateAction, setDuplicateAction] = useState('skip');
  const [importResult, setImportResult] = useState(null);
  const [showImportResult, setShowImportResult] = useState(false);

  // Paramètres app (sources, intérêts, statuts, etc.)
  const [appSettings, setAppSettings] = useState({ 
    contact_sources: [], 
    contact_interets: [], 
    contact_statuts: [],
    contact_degres_interet: []
  });

  useEffect(() => {
    verifyAuth();
    fetchAppSettings();
  }, []);

  const fetchAppSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/app`, getAuthHeaders());
      setAppSettings(response.data);
    } catch (error) {
      console.error('Erreur chargement paramètres app:', error);
    }
  };

  useEffect(() => {
    if (user) {
      fetchContacts();
      fetchStats();
    }
  }, [currentPage, filterStatut, filterSource, filterTags, user]);
  
  // Debounce pour la recherche (300ms)
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  
  useEffect(() => {
    if (user && debouncedSearchTerm !== undefined) {
      setCurrentPage(1);
      fetchContacts();
    }
  }, [debouncedSearchTerm]);

  const verifyAuth = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      toast.error('Session expirée. Veuillez vous reconnecter.');
      localStorage.removeItem('admin_token');
      navigate('/login');
    }
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const handleBulkConsent = async () => {
    if (!window.confirm('Voulez-vous activer le consentement marketing pour tous les contacts qui ne l\'ont pas encore? Cela permettra de les inclure dans les campagnes email.')) {
      return;
    }
    
    try {
      const response = await axios.post(`${API}/contacts/bulk-consent`, 
        { 
          filter_type: 'no_consent', 
          consent_source: 'Activation en masse - Admin' 
        },
        getAuthHeaders()
      );
      
      toast.success(`Consentement activé pour ${response.data.updated_count} contacts`);
      fetchContacts();
      fetchStats();
    } catch (error) {
      console.error('Erreur activation consentement:', error);
      toast.error('Erreur lors de l\'activation du consentement');
    }
  };

  const fetchContacts = async () => {
    try {
      const skip = (currentPage - 1) * contactsPerPage;
      const response = await axios.get(`${API}/contacts`, {
        ...getAuthHeaders(),
        params: {
          skip,
          limit: contactsPerPage,
          search: debouncedSearchTerm || searchTerm,
          statut: filterStatut,
          source: filterSource,
          tags: filterTags
        }
      });
      setContacts(response.data.contacts);
      setTotalContacts(response.data.total);
    } catch (error) {
      console.error('Erreur chargement contacts:', error);
      toast.error('Erreur lors du chargement des contacts');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/contacts/stats`, getAuthHeaders());
      setStats(response.data);
    } catch (error) {
      console.error('Erreur chargement stats:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/login');
  };

  const openModal = async (contact = null) => {
    if (contact) {
      setSelectedContact(contact);
      // Préserver les valeurs existantes du contact
      setFormData({
        prenom: contact.prenom || '',
        nom: contact.nom || '',
        email: contact.email || '',
        telephone: contact.telephone || '',
        address: contact.address || {
          street: '',
          city: '',
          province: 'Québec',
          postal_code: ''
        },
        statut: contact.statut || 'prospect',  // Valeur par défaut si vide
        degre_interet: contact.degre_interet || '',
        tags: contact.tags || [],
        interets: contact.interets || [],
        notes: contact.notes || '',
        source: contact.source || 'manuel',  // Valeur par défaut si vide
        marketing_consent: contact.marketing_consent || false,
        consent_source: contact.consent_source || 'manuel'
      });
      
      // Charger les deals du contact (sans bloquer l'ouverture de la modal)
      setContactDeals([]);  // Reset d'abord
      setShowModal(true);   // Ouvrir la modal tout de suite
      
      try {
        const dealsResponse = await axios.get(`${API}/deals?contact_id=${contact.id}`, getAuthHeaders());
        setContactDeals(dealsResponse.data.deals || []);
      } catch (error) {
        console.error('Erreur chargement deals:', error);
        setContactDeals([]);
      }
    } else {
      setSelectedContact(null);
      setContactDeals([]);
      setShowModal(true);
      // Utiliser la première source disponible ou 'manuel' par défaut
      const defaultSource = appSettings.contact_sources && appSettings.contact_sources.length > 0 
        ? appSettings.contact_sources[0] 
        : 'manuel';
      const defaultStatut = appSettings.contact_statuts && appSettings.contact_statuts.length > 0 
        ? appSettings.contact_statuts[0] 
        : 'prospect';
      setFormData({
        prenom: '',
        nom: '',
        email: '',
        telephone: '',
        address: {
          street: '',
          city: '',
          province: 'Québec',
          postal_code: ''
        },
        statut: defaultStatut,
        degre_interet: '',
        tags: [],
        interets: [],
        notes: '',
        source: defaultSource,
        marketing_consent: false,
        consent_source: 'manuel'
      });
      setShowModal(true);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedContact(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (selectedContact) {
        // Mise à jour
        await axios.patch(`${API}/contacts/${selectedContact.id}`, formData, getAuthHeaders());
        toast.success('Contact mis à jour avec succès');
      } else {
        // Création
        await axios.post(`${API}/contacts`, formData, getAuthHeaders());
        toast.success('Contact créé avec succès');
      }
      
      closeModal();
      fetchContacts();
      fetchStats();
    } catch (error) {
      console.error('Erreur sauvegarde contact:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (contactId, contactName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le contact ${contactName} ?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/contacts/${contactId}`, getAuthHeaders());
      toast.success('Contact supprimé avec succès');
      fetchContacts();
      fetchStats();
    } catch (error) {
      console.error('Erreur suppression:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  // === GESTION CONSENTEMENT MARKETING (CASL) ===
  const handleUnsubscribe = async (contact) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir désinscrire ${contact.prenom} ${contact.nom} des communications marketing?\n\nCette action annulera également son consentement marketing.`)) {
      return;
    }

    try {
      await axios.post(`${API}/contacts/${contact.id}/unsubscribe`, {}, getAuthHeaders());
      toast.success(`${contact.prenom} ${contact.nom} a été désinscrit avec succès`);
      fetchContacts();
    } catch (error) {
      console.error('Erreur désinscription:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la désinscription');
    }
  };

  const handleConsentToggle = async (contact, newConsentValue) => {
    try {
      if (newConsentValue) {
        // Donner le consentement
        await axios.post(
          `${API}/contacts/${contact.id}/consent`, 
          { consent_source: 'manuel' },
          getAuthHeaders()
        );
        toast.success(`Consentement marketing activé pour ${contact.prenom} ${contact.nom}`);
      } else {
        // Retirer le consentement (via update)
        await axios.patch(
          `${API}/contacts/${contact.id}`,
          { marketing_consent: false },
          getAuthHeaders()
        );
        toast.success(`Consentement marketing retiré pour ${contact.prenom} ${contact.nom}`);
      }
      fetchContacts();
    } catch (error) {
      console.error('Erreur consentement:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour du consentement');
    }
  };

  // Ouvrir l'assistant IA pour un contact
  const openAIAssistant = async (contact) => {
    // Chercher s'il y a un deal actif pour ce contact
    try {
      const dealsResponse = await axios.get(`${API}/deals?contact_id=${contact.id}`, getAuthHeaders());
      const activeDeal = dealsResponse.data.deals?.find(d => !['won', 'lost', 'delivered'].includes(d.stage));
      
      setAiContactId(contact.id);
      setAiContactName(`${contact.prenom} ${contact.nom}`);
      setAiDealId(activeDeal?.id || null);
      setShowAIAssistant(true);
    } catch (error) {
      // Continuer sans deal
      setAiContactId(contact.id);
      setAiContactName(`${contact.prenom} ${contact.nom}`);
      setAiDealId(null);
      setShowAIAssistant(true);
    }
  };

  const handleImportExcel = async () => {
    if (!importFile) {
      toast.error('Veuillez sélectionner un fichier Excel');
      return;
    }

    try {
      // Créer un FormData pour envoyer le fichier
      const formData = new FormData();
      formData.append('file', importFile);

      const response = await axios.post(
        `${API}/contacts/import-excel?duplicate_action=${duplicateAction}`,
        formData,
        {
          headers: {
            ...getAuthHeaders().headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      const result = response.data;
      
      // Stocker le résultat pour affichage détaillé
      setImportResult(result);
      setShowImportResult(true);
      
      if (result.imported > 0) {
        toast.success(`${result.imported} contacts importés avec succès`);
      }
      
      if (result.errors > 0) {
        toast.warning(`${result.errors} erreurs détectées`);
      }

      setImportFile(null);
      fetchContacts();
      fetchStats();
    } catch (error) {
      console.error('Erreur import:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'import');
    }
  };

  const handleExportExcel = async () => {
    try {
      const response = await axios.get(`${API}/contacts/export-excel`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'contacts_refacing_vr.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Export Excel téléchargé');
    } catch (error) {
      console.error('Erreur export:', error);
      toast.error('Erreur lors de l\'export');
    }
  };

  const addTag = (tag) => {
    if (tag && !formData.tags.includes(tag)) {
      setFormData({ ...formData, tags: [...formData.tags, tag] });
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData({ ...formData, tags: formData.tags.filter(t => t !== tagToRemove) });
  };

  const toggleInteret = (interet) => {
    if (formData.interets.includes(interet)) {
      setFormData({ ...formData, interets: formData.interets.filter(i => i !== interet) });
    } else {
      setFormData({ ...formData, interets: [...formData.interets, interet] });
    }
  };

  const totalPages = Math.ceil(totalContacts / contactsPerPage);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F5F5F5' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderColor: '#335C2E' }}></div>
          <p className="mt-4" style={{ color: '#666666' }}>Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#F5F5F5' }}>
      {/* Header */}
      <header className="shadow-sm" style={{ background: '#FFFFFF' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold" style={{ color: '#335C2E' }}>
                CRM Refacing VR
              </h1>
              {stats && (
                <span className="px-3 py-1 rounded-full text-sm font-medium" style={{ background: '#E8F5E9', color: '#2E7D32' }}>
                  {stats.total} contacts
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/admin')}
                className="flex items-center gap-2 px-4 py-2 rounded hover:opacity-90 transition-opacity"
                style={{ background: '#335C2E', color: '#FFFFFF' }}
              >
                <Home size={18} />
                Retour Admin
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 rounded hover:opacity-90 transition-opacity"
                style={{ background: '#DC3545', color: '#FFFFFF' }}
              >
                <LogOut size={18} />
                Déconnexion
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Statistiques */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {/* Utiliser les statuts configurés ou les défauts */}
            {(appSettings.contact_statuts && appSettings.contact_statuts.length > 0 
              ? appSettings.contact_statuts 
              : ['prospect', 'client', 'lead', 'inactif']
            ).slice(0, 4).map((statut, index) => {
              const colors = ['#335C2E', '#C7A027', '#2196F3', '#999999'];
              const count = stats.by_statut?.[statut.toLowerCase()] || 0;
              return (
                <div key={statut} className="p-4 rounded-lg shadow" style={{ background: '#FFFFFF' }}>
                  <div className="text-sm capitalize" style={{ color: '#666666' }}>{statut}</div>
                  <div className="text-2xl font-bold mt-1" style={{ color: colors[index % colors.length] }}>
                    {count}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Actions et Filtres */}
        <div className="p-6 rounded-lg shadow mb-6" style={{ background: '#FFFFFF' }}>
          <div className="flex flex-wrap items-center gap-4 mb-4">
            <button
              onClick={() => openModal()}
              className="flex items-center gap-2 px-4 py-2 rounded hover:opacity-90 transition-opacity"
              style={{ background: '#335C2E', color: '#FFFFFF' }}
            >
              <Plus size={18} />
              Nouveau contact
            </button>
            
            <button
              onClick={handleExportExcel}
              className="flex items-center gap-2 px-4 py-2 rounded hover:opacity-90 transition-opacity"
              style={{ background: '#C7A027', color: '#FFFFFF' }}
            >
              <Download size={18} />
              Exporter Excel
            </button>

            <button
              onClick={handleBulkConsent}
              className="flex items-center gap-2 px-4 py-2 rounded hover:opacity-90 transition-opacity"
              style={{ background: '#9C27B0', color: '#FFFFFF' }}
              title="Activer le consentement marketing pour tous les contacts sans consentement"
            >
              <Users size={18} />
              Activer consentements
            </button>

            <div className="flex items-center gap-2">
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setImportFile(e.target.files[0])}
                className="hidden"
                id="excel-import"
              />
              <label
                htmlFor="excel-import"
                className="flex items-center gap-2 px-4 py-2 rounded cursor-pointer hover:opacity-90 transition-opacity"
                style={{ background: '#2196F3', color: '#FFFFFF' }}
              >
                <Upload size={18} />
                Sélectionner Excel
              </label>
              
              {importFile && (
                <>
                  <span className="text-sm" style={{ color: '#666666' }}>
                    {importFile.name}
                  </span>
                  <select
                    value={duplicateAction}
                    onChange={(e) => setDuplicateAction(e.target.value)}
                    className="px-2 py-1 border rounded text-sm"
                  >
                    <option value="skip">Ignorer doublons</option>
                    <option value="replace">Remplacer doublons</option>
                    <option value="error">Erreur si doublon</option>
                  </select>
                  <button
                    onClick={handleImportExcel}
                    className="px-4 py-2 rounded hover:opacity-90 transition-opacity"
                    style={{ background: '#4CAF50', color: '#FFFFFF' }}
                  >
                    Importer
                  </button>
                  <button
                    onClick={() => setImportFile(null)}
                    className="px-2 py-2 rounded hover:opacity-90 transition-opacity"
                    style={{ background: '#DC3545', color: '#FFFFFF' }}
                  >
                    <X size={18} />
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Filtres */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: '#333333' }}>
                <Search className="inline-block mr-1" size={16} />
                Recherche
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Nom, email, téléphone..."
                className="w-full p-2 border rounded"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: '#333333' }}>
                <Filter className="inline-block mr-1" size={16} />
                Statut
              </label>
              <select
                value={filterStatut}
                onChange={(e) => {
                  setFilterStatut(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full p-2 border rounded"
              >
                <option value="">Tous</option>
                {appSettings.contact_statuts && appSettings.contact_statuts.map((statut) => (
                  <option key={statut} value={statut}>{statut}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: '#333333' }}>
                Source
              </label>
              <select
                value={filterSource}
                onChange={(e) => {
                  setFilterSource(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full p-2 border rounded"
              >
                <option value="">Toutes</option>
                {appSettings.contact_sources && appSettings.contact_sources.map((source) => (
                  <option key={source} value={source}>{source}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: '#333333' }}>
                Tags
              </label>
              <input
                type="text"
                value={filterTags}
                onChange={(e) => {
                  setFilterTags(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Séparés par virgule"
                className="w-full p-2 border rounded"
              />
            </div>
          </div>

          {(searchTerm || filterStatut || filterSource || filterTags) && (
            <button
              onClick={() => {
                setSearchTerm('');
                setFilterStatut('');
                setFilterSource('');
                setFilterTags('');
                setCurrentPage(1);
              }}
              className="mt-3 text-sm px-3 py-1 rounded hover:bg-gray-200"
              style={{ color: '#335C2E' }}
            >
              ✕ Réinitialiser les filtres
            </button>
          )}
        </div>

        {/* Liste des contacts */}
        <div className="rounded-lg shadow overflow-hidden" style={{ background: '#FFFFFF' }}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead style={{ background: '#F5F5F5' }}>
                <tr>
                  <th className="text-left p-3 font-semibold" style={{ color: '#333333' }}>Contact</th>
                  <th className="text-left p-3 font-semibold" style={{ color: '#333333' }}>Coordonnées</th>
                  <th className="text-left p-3 font-semibold" style={{ color: '#333333' }}>Statut</th>
                  <th className="text-left p-3 font-semibold" style={{ color: '#333333' }}>Source</th>
                  <th className="text-center p-3 font-semibold" style={{ color: '#333333' }} title="Consentement marketing (CASL)">Consent.</th>
                  <th className="text-left p-3 font-semibold" style={{ color: '#333333' }}>Tags</th>
                  <th className="text-center p-3 font-semibold" style={{ color: '#333333' }}>Campagnes</th>
                  <th className="text-center p-3 font-semibold" style={{ color: '#333333' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((contact) => (
                  <tr key={contact.id} className="border-b hover:bg-gray-50">
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: '#E8F5E9', color: '#2E7D32' }}>
                          <User size={20} />
                        </div>
                        <div>
                          <div className="font-medium flex items-center gap-2" style={{ color: '#333333' }}>
                            {contact.prenom} {contact.nom}
                            {/* Badge désinscrit */}
                            {contact.unsubscribed && (
                              <span 
                                className="px-1.5 py-0.5 rounded text-xs font-medium"
                                style={{ background: '#FFEBEE', color: '#C62828' }}
                                title={`Désinscrit le ${contact.unsubscribed_date ? new Date(contact.unsubscribed_date).toLocaleDateString('fr-CA') : 'date inconnue'}`}
                              >
                                <ShieldOff size={10} className="inline mr-1" />
                                Désinscrit
                              </span>
                            )}
                          </div>
                          {contact.address?.city && (
                            <div className="text-xs" style={{ color: '#999999' }}>
                              {contact.address.city}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex flex-col gap-1 text-sm">
                        <div className="flex items-center gap-1" style={{ color: '#666666' }}>
                          <Mail size={14} />
                          {contact.email}
                        </div>
                        {contact.telephone && (
                          <div className="flex items-center gap-1" style={{ color: '#666666' }}>
                            <Phone size={14} />
                            {contact.telephone}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="p-3">
                      <span
                        className="px-2 py-1 rounded text-xs font-medium"
                        style={{
                          background: contact.statut === 'client' ? '#FFF3E0' : 
                                     contact.statut === 'prospect' ? '#E8F5E9' :
                                     contact.statut === 'lead' ? '#E3F2FD' : '#F5F5F5',
                          color: contact.statut === 'client' ? '#E65100' : 
                                 contact.statut === 'prospect' ? '#2E7D32' :
                                 contact.statut === 'lead' ? '#1976D2' : '#666666'
                        }}
                      >
                        {contact.statut}
                      </span>
                    </td>
                    <td className="p-3 text-sm" style={{ color: '#666666' }}>
                      {contact.source || '-'}
                    </td>
                    {/* Colonne Consentement Marketing */}
                    <td className="p-3 text-center">
                      {contact.unsubscribed ? (
                        <span 
                          className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium"
                          style={{ background: '#FFEBEE', color: '#C62828' }}
                          title="Ce contact s'est désinscrit"
                        >
                          <XCircle size={12} />
                        </span>
                      ) : contact.marketing_consent ? (
                        <span 
                          className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium cursor-pointer"
                          style={{ background: '#E8F5E9', color: '#2E7D32' }}
                          title={`Consentement donné${contact.consent_source ? ` (${contact.consent_source})` : ''}\n${contact.marketing_consent_date ? new Date(contact.marketing_consent_date).toLocaleDateString('fr-CA') : ''}`}
                          onClick={() => handleConsentToggle(contact, false)}
                        >
                          <CheckCircle size={12} />
                          Oui
                        </span>
                      ) : (
                        <span 
                          className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium cursor-pointer"
                          style={{ background: '#F5F5F5', color: '#999999' }}
                          title="Cliquez pour donner le consentement"
                          onClick={() => handleConsentToggle(contact, true)}
                        >
                          <XCircle size={12} />
                          Non
                        </span>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex flex-wrap gap-1">
                        {contact.tags?.slice(0, 2).map((tag, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-0.5 rounded text-xs"
                            style={{ background: '#E8F5E9', color: '#2E7D32' }}
                          >
                            {tag}
                          </span>
                        ))}
                        {contact.tags?.length > 2 && (
                          <span className="text-xs" style={{ color: '#999999' }}>
                            +{contact.tags.length - 2}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-3 text-center">
                      {contact.campaigns_received?.length > 0 ? (
                        <span 
                          className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium cursor-pointer"
                          style={{ background: '#E3F2FD', color: '#1976D2' }}
                          title={contact.campaigns_received.map(c => c.campaign_name || 'Sans nom').join('\n')}
                        >
                          <Mail size={12} />
                          {contact.campaigns_received.length}
                        </span>
                      ) : (
                        <span className="text-xs" style={{ color: '#999999' }}>-</span>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex gap-1 justify-center">
                        <button
                          onClick={() => openModal(contact)}
                          className="p-2 rounded hover:bg-gray-200"
                          title="Modifier"
                        >
                          <Edit size={16} style={{ color: '#335C2E' }} />
                        </button>
                        {/* Bouton Assistant IA */}
                        <button
                          onClick={() => openAIAssistant(contact)}
                          className="p-2 rounded hover:bg-purple-50"
                          title="Assistant IA"
                        >
                          <Bot size={16} style={{ color: '#673AB7' }} />
                        </button>
                        {/* Bouton Désinscrire - visible seulement si pas déjà désinscrit */}
                        {!contact.unsubscribed && (
                          <button
                            onClick={() => handleUnsubscribe(contact)}
                            className="p-2 rounded hover:bg-red-50"
                            title="Désinscrire des communications marketing"
                          >
                            <ShieldOff size={16} style={{ color: '#E65100' }} />
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(contact.id, `${contact.prenom} ${contact.nom}`)}
                          className="p-2 rounded hover:bg-gray-200"
                          title="Supprimer"
                        >
                          <Trash2 size={16} style={{ color: '#DC3545' }} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {contacts.length === 0 && (
              <div className="text-center py-12" style={{ color: '#999999' }}>
                Aucun contact trouvé
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between p-4 border-t">
              <div className="text-sm" style={{ color: '#666666' }}>
                Page {currentPage} sur {totalPages} ({totalContacts} contacts)
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 rounded border disabled:opacity-50"
                  style={{ color: '#335C2E', borderColor: '#335C2E' }}
                >
                  Précédent
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 rounded border disabled:opacity-50"
                  style={{ color: '#335C2E', borderColor: '#335C2E' }}
                >
                  Suivant
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal Formulaire */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold" style={{ color: '#335C2E' }}>
                {selectedContact ? 'Modifier le contact' : 'Nouveau contact'}
              </h2>
              <button onClick={closeModal} className="p-2 hover:bg-gray-100 rounded">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Prénom *</label>
                  <input
                    type="text"
                    value={formData.prenom}
                    onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                    required
                    className="w-full p-2 border rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Nom *</label>
                  <input
                    type="text"
                    value={formData.nom}
                    onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    required
                    className="w-full p-2 border rounded"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="w-full p-2 border rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Téléphone</label>
                  <input
                    type="tel"
                    value={formData.telephone}
                    onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                    className="w-full p-2 border rounded"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Adresse</label>
                <input
                  type="text"
                  value={formData.address.street}
                  onChange={(e) => setFormData({ ...formData, address: { ...formData.address, street: e.target.value } })}
                  placeholder="Rue"
                  className="w-full p-2 border rounded mb-2"
                />
                <div className="grid grid-cols-3 gap-2">
                  <input
                    type="text"
                    value={formData.address.city}
                    onChange={(e) => setFormData({ ...formData, address: { ...formData.address, city: e.target.value } })}
                    placeholder="Ville"
                    className="w-full p-2 border rounded"
                  />
                  <input
                    type="text"
                    value={formData.address.province}
                    onChange={(e) => setFormData({ ...formData, address: { ...formData.address, province: e.target.value } })}
                    placeholder="Province"
                    className="w-full p-2 border rounded"
                  />
                  <input
                    type="text"
                    value={formData.address.postal_code}
                    onChange={(e) => setFormData({ ...formData, address: { ...formData.address, postal_code: e.target.value } })}
                    placeholder="Code postal"
                    className="w-full p-2 border rounded"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Statut *</label>
                  <select
                    value={formData.statut}
                    onChange={(e) => setFormData({ ...formData, statut: e.target.value })}
                    className="w-full p-2 border rounded"
                    required
                  >
                    {/* Si la valeur actuelle n'est pas dans la liste, l'ajouter */}
                    {formData.statut && 
                     !appSettings.contact_statuts.includes(formData.statut) && 
                     !['prospect', 'client', 'lead', 'inactif'].includes(formData.statut) && (
                      <option value={formData.statut}>{formData.statut} (valeur actuelle)</option>
                    )}
                    {appSettings.contact_statuts.length > 0 ? (
                      appSettings.contact_statuts.map((statut) => (
                        <option key={statut} value={statut}>{statut}</option>
                      ))
                    ) : (
                      <>
                        <option value="prospect">Prospect</option>
                        <option value="client">Client</option>
                        <option value="lead">Lead</option>
                        <option value="inactif">Inactif</option>
                      </>
                    )}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Degré d&apos;intérêt</label>
                  <select
                    value={formData.degre_interet}
                    onChange={(e) => setFormData({ ...formData, degre_interet: e.target.value })}
                    className="w-full p-2 border rounded"
                  >
                    <option value="">-- Non défini --</option>
                    {/* Si la valeur actuelle n'est pas dans la liste, l'ajouter */}
                    {formData.degre_interet && 
                     !appSettings.contact_degres_interet.includes(formData.degre_interet) && 
                     !['Chaud', 'Tiède', 'Froid'].includes(formData.degre_interet) && (
                      <option value={formData.degre_interet}>{formData.degre_interet} (valeur actuelle)</option>
                    )}
                    {appSettings.contact_degres_interet.length > 0 ? (
                      appSettings.contact_degres_interet.map((degre) => (
                        <option key={degre} value={degre}>{degre}</option>
                      ))
                    ) : (
                      <>
                        <option value="Chaud">Chaud</option>
                        <option value="Tiède">Tiède</option>
                        <option value="Froid">Froid</option>
                      </>
                    )}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Source</label>
                  <select
                    value={formData.source}
                    onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                    className="w-full p-2 border rounded"
                  >
                    <option value="">-- Sélectionner une source --</option>
                    {/* Si la valeur actuelle n'est pas dans la liste, l'ajouter */}
                    {formData.source && 
                     formData.source !== '' &&
                     !(appSettings.contact_sources || []).includes(formData.source) && 
                     !['manuel', 'bd_interne', 'formulaire_web', 'référence', 'publicité', 'réseaux_sociaux', 'événement', 'autre', 'excel_import'].includes(formData.source) && (
                      <option value={formData.source}>{formData.source} (valeur importée)</option>
                    )}
                    {(appSettings.contact_sources && appSettings.contact_sources.length > 0 
                      ? appSettings.contact_sources 
                      : ['manuel', 'bd_interne', 'excel_import', 'formulaire_web', 'référence', 'publicité', 'réseaux_sociaux', 'événement', 'autre']
                    ).map((source) => (
                      <option key={source} value={source}>{source}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Tags</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {formData.tags.map((tag, idx) => (
                    <span
                      key={idx}
                      className="px-2 py-1 rounded text-sm flex items-center gap-1"
                      style={{ background: '#E8F5E9', color: '#2E7D32' }}
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="hover:opacity-70"
                      >
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Ajouter un tag..."
                    className="flex-1 p-2 border rounded"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addTag(e.target.value.trim());
                        e.target.value = '';
                      }
                    }}
                    id="tag-input"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const input = document.getElementById('tag-input');
                      addTag(input.value.trim());
                      input.value = '';
                    }}
                    className="px-3 py-2 rounded"
                    style={{ background: '#335C2E', color: '#FFFFFF' }}
                  >
                    <Plus size={16} />
                  </button>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Intérêts</label>
                <p className="text-xs text-gray-500 mb-2">Sélectionnez les projets qui intéressent ce contact</p>
                <div className="flex flex-wrap gap-2">
                  {appSettings.contact_interets.map((interet) => (
                    <button
                      key={interet}
                      type="button"
                      onClick={() => toggleInteret(interet)}
                      className={`px-3 py-1.5 rounded text-sm transition-colors ${
                        formData.interets.includes(interet)
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {interet}
                    </button>
                  ))}
                </div>
                {formData.interets.length === 0 && (
                  <p className="text-xs text-gray-400 mt-2">Aucun intérêt sélectionné</p>
                )}
              </div>

              {/* Section Consentement Marketing (CASL) */}
              <div className="mb-4 p-4 rounded-lg" style={{ background: '#F5F5F5' }}>
                <div className="flex items-center gap-2 mb-2">
                  <Shield size={18} style={{ color: '#335C2E' }} />
                  <label className="font-medium" style={{ color: '#333333' }}>Consentement Marketing (CASL)</label>
                </div>
                
                {/* Badge si désinscrit */}
                {selectedContact?.unsubscribed ? (
                  <div className="p-3 rounded-lg mb-3" style={{ background: '#FFEBEE' }}>
                    <div className="flex items-center gap-2">
                      <ShieldOff size={16} style={{ color: '#C62828' }} />
                      <span className="text-sm font-medium" style={{ color: '#C62828' }}>
                        Ce contact s&apos;est désinscrit le {selectedContact.unsubscribed_date ? new Date(selectedContact.unsubscribed_date).toLocaleDateString('fr-CA') : 'date inconnue'}
                      </span>
                    </div>
                    <p className="text-xs mt-1" style={{ color: '#666666' }}>
                      Pour réactiver les communications, le contact doit donner un nouveau consentement explicite.
                    </p>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.marketing_consent || false}
                        onChange={(e) => setFormData({ ...formData, marketing_consent: e.target.checked })}
                        className="w-4 h-4 rounded"
                        style={{ accentColor: '#335C2E' }}
                      />
                      <span className="text-sm">Ce contact consent à recevoir des communications marketing</span>
                    </label>
                  </div>
                )}
                
                {formData.marketing_consent && !selectedContact?.unsubscribed && (
                  <div className="mt-3">
                    <label className="block text-sm mb-1" style={{ color: '#666666' }}>Source du consentement</label>
                    <select
                      value={formData.consent_source || 'manuel'}
                      onChange={(e) => setFormData({ ...formData, consent_source: e.target.value })}
                      className="w-full p-2 border rounded text-sm"
                    >
                      <option value="manuel">Manuel (admin)</option>
                      <option value="site">Site web</option>
                      <option value="portes_ouvertes">Portes ouvertes</option>
                      <option value="import">Import Excel</option>
                      <option value="formulaire">Formulaire</option>
                    </select>
                  </div>
                )}
                
                <p className="text-xs mt-2" style={{ color: '#999999' }}>
                  ℹ️ Conformément à la loi CASL (Canada), seuls les contacts avec consentement peuvent recevoir des communications marketing.
                </p>
              </div>

              {/* Section Dossiers (Deals) - Vague 4 */}
              {selectedContact && (
                <div className="mb-6 p-4 rounded-lg border" style={{ background: '#FAFAFA' }}>
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Target size={16} style={{ color: '#673AB7' }} />
                    Dossiers (Deals)
                  </h4>
                  
                  {contactDeals.length === 0 ? (
                    <p className="text-sm" style={{ color: '#999999' }}>Aucun deal pour ce contact</p>
                  ) : (
                    <div className="space-y-2">
                      {contactDeals.map(deal => (
                        <div 
                          key={deal.id}
                          className="p-3 bg-white rounded border flex items-center justify-between hover:shadow-sm cursor-pointer"
                          onClick={() => navigate(`/admin/pipeline`)}
                        >
                          <div>
                            <p className="font-medium text-sm">{deal.title}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <span 
                                className="px-2 py-0.5 rounded text-xs"
                                style={{ 
                                  background: deal.stage === 'won' ? '#E8F5E9' : 
                                             deal.stage === 'lost' ? '#FFEBEE' : '#F5F5F5',
                                  color: deal.stage === 'won' ? '#2E7D32' : 
                                         deal.stage === 'lost' ? '#C62828' : '#666666'
                                }}
                              >
                                {deal.stage?.toUpperCase()}
                              </span>
                              {deal.estimated_value && (
                                <span className="text-xs" style={{ color: '#C7A027' }}>
                                  {new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }).format(deal.estimated_value)}
                                </span>
                              )}
                              {deal.active_submission_id && (
                                <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: '#E3F2FD', color: '#1976D2' }}>
                                  📄 Soumission
                                </span>
                              )}
                              {deal.contract_id && (
                                <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: '#E8F5E9', color: '#2E7D32' }}>
                                  📝 Contrat
                                </span>
                              )}
                            </div>
                          </div>
                          <ChevronRight size={16} style={{ color: '#999999' }} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="mb-6">
                <label className="block text-sm font-medium mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows="4"
                  className="w-full p-2 border rounded"
                  placeholder="Notes additionnelles..."
                />
              </div>

              <div className="flex gap-3 justify-end">
                {/* Bouton Créer Deal (seulement en mode édition) */}
                {selectedContact && (
                  <button
                    type="button"
                    onClick={async () => {
                      if (!window.confirm(`Créer une nouvelle opportunité (Deal) pour ${selectedContact.prenom} ${selectedContact.nom}?`)) return;
                      
                      try {
                        const dealTitle = `${formData.interets?.[0] || 'UHA'} - ${formData.address?.city || selectedContact.prenom} - ${new Date().getFullYear()}`;
                        const response = await axios.post(`${API}/deals`, {
                          contact_id: selectedContact.id,
                          title: dealTitle,
                          deal_type: formData.interets?.includes('Maison de parc') ? 'maison_parc' : 'UHA',
                          stage: 'lead',
                          source: formData.source,
                          project_city: formData.address?.city
                        }, getAuthHeaders());
                        
                        toast.success(`Deal créé: ${response.data.title}`);
                        closeModal();
                        // Ouvrir le pipeline sur le nouveau deal
                        navigate(`/admin/pipeline`);
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Erreur lors de la création du deal');
                      }
                    }}
                    className="flex items-center gap-2 px-4 py-2 rounded hover:opacity-90"
                    style={{ background: '#673AB7', color: '#FFFFFF' }}
                    title="Créer une opportunité commerciale"
                  >
                    <Target size={18} />
                    Créer Deal
                  </button>
                )}
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 rounded border"
                  style={{ color: '#666666', borderColor: '#E5E5E5' }}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex items-center gap-2 px-4 py-2 rounded hover:opacity-90"
                  style={{ background: '#335C2E', color: '#FFFFFF' }}
                >
                  <Save size={18} />
                  {selectedContact ? 'Mettre à jour' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Résultats Import Excel */}
      {showImportResult && importResult && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 border-b" style={{ background: '#335C2E' }}>
              <h2 className="text-xl font-bold text-white">Résultat de l&apos;import Excel</h2>
            </div>

            {/* Statistiques */}
            <div className="p-6 border-b">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-3 rounded-lg" style={{ background: '#E8F5E9' }}>
                  <p className="text-2xl font-bold" style={{ color: '#2E7D32' }}>{importResult.imported}</p>
                  <p className="text-sm text-gray-600">Importés</p>
                </div>
                <div className="text-center p-3 rounded-lg" style={{ background: '#FFF3E0' }}>
                  <p className="text-2xl font-bold" style={{ color: '#E65100' }}>{importResult.duplicates}</p>
                  <p className="text-sm text-gray-600">Doublons</p>
                </div>
                <div className="text-center p-3 rounded-lg" style={{ background: '#FFEBEE' }}>
                  <p className="text-2xl font-bold" style={{ color: '#C62828' }}>{importResult.errors}</p>
                  <p className="text-sm text-gray-600">Erreurs</p>
                </div>
                <div className="text-center p-3 rounded-lg" style={{ background: '#E3F2FD' }}>
                  <p className="text-2xl font-bold" style={{ color: '#1565C0' }}>{importResult.total_rows}</p>
                  <p className="text-sm text-gray-600">Total lignes</p>
                </div>
              </div>
            </div>

            {/* Détails des erreurs */}
            {importResult.error_details && importResult.error_details.length > 0 && (
              <div className="p-6 overflow-y-auto max-h-64">
                <h3 className="font-semibold mb-3 flex items-center gap-2" style={{ color: '#C62828' }}>
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  Détails des erreurs ({importResult.error_details.length})
                </h3>
                <div className="space-y-2">
                  {importResult.error_details.map((error, idx) => (
                    <div
                      key={idx}
                      className="p-3 rounded-lg text-sm border-l-4"
                      style={{ 
                        background: '#FFF5F5', 
                        borderColor: '#C62828',
                        color: '#333333'
                      }}
                    >
                      {error}
                    </div>
                  ))}
                </div>
                {importResult.errors > importResult.error_details.length && (
                  <p className="text-sm text-gray-500 mt-3 italic">
                    ... et {importResult.errors - importResult.error_details.length} autres erreurs
                  </p>
                )}
              </div>
            )}

            {/* Message si pas d'erreurs */}
            {importResult.errors === 0 && (
              <div className="p-6 text-center">
                <svg className="w-16 h-16 mx-auto mb-4" style={{ color: '#4CAF50' }} fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <p className="text-lg font-medium" style={{ color: '#2E7D32' }}>
                  Import réussi sans erreur !
                </p>
              </div>
            )}

            {/* Footer */}
            <div className="px-6 py-4 border-t bg-gray-50 flex justify-end">
              <button
                onClick={() => {
                  setShowImportResult(false);
                  setImportResult(null);
                }}
                className="px-6 py-2 rounded-lg text-white hover:opacity-90"
                style={{ background: '#335C2E' }}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Assistant IA */}
      {showAIAssistant && (
        <AIAssistant
          contactId={aiContactId}
          contactName={aiContactName}
          dealId={aiDealId}
          onClose={() => setShowAIAssistant(false)}
          getAuthHeaders={getAuthHeaders}
        />
      )}
    </div>
  );
};

export default CRM;
